import { useEffect } from 'react'
import { Alert, Form, Input, Modal, Switch } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  JOB_TITLE_CODE_PATTERN,
  NAME_MAX,
  createJobTitle,
  updateJobTitle,
  type JobTitle,
} from './hrApi'

interface JobTitleFormModalProps {
  open: boolean
  /** `null` = yangi lavozim */
  jobTitle: JobTitle | null
  onClose: () => void
}

interface FormValues {
  code: string
  name: string
  active: boolean
}

/**
 * Lavozim katalogi — yaratish va tahrirlash.
 *
 * <h2>⚠️ O'CHIRISH YO'LI YO'Q va bo'lmaydi ham</h2>
 * `employment_job_title_fk` `RESTRICT` (`JobTitleDraft` javadoc'i):
 * lavozimni o'chirish o'tgan davrlarni NOMSIZ qoldirardi va maosh
 * hisoboti "2024-yil martda bu odam qaysi lavozimda edi" savoliga
 * javob bera olmasdi.
 *
 * O'rniga `active = false`: yangi tayinlashda tanlanmaydi, MAVJUD
 * tarixda qoladi. Modal buni ochiq tushuntiradi, aks holda operator
 * o'chirish tugmasini qidirib yurardi.
 *
 * <h2>⚠️ KOD — ISHORA, QOIDA EMAS</h2>
 * `code` na panelda, na backendda satr bilan solishtiriladi. Lavozim
 * biror qoidani boshqaradigan bo'lsa (masalan "yarim stavkaga ta'til
 * yarim hisoblanadi"), u `org_node_type` naqshidagi IMKONIYAT
 * ustuniga o'tkaziladi, `if` ga emas (§19.3 bilan bir xil qoida).
 */
export function JobTitleFormModal({ open, jobTitle, onClose }: JobTitleFormModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  const isEdit = jobTitle !== null

  useEffect(() => {
    if (!open) return
    form.setFieldsValue(
      jobTitle
        ? { code: jobTitle.code, name: jobTitle.name, active: jobTitle.active }
        : { code: '', name: '', active: true },
    )
  }, [open, jobTitle, form])

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const draft = {
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        active: values.active,
      }
      return isEdit && jobTitle ? updateJobTitle(jobTitle.id, draft) : createJobTitle(draft)
    },
    onSuccess: () => {
      // ⚠️ `hr.all` PREFIKS: katalog ikki ro'yxatda ko'rinadi
      // (`activeOnly=true` va `false`) va ular ALOHIDA kesh yozuvi.
      // Faqat bittasini bekor qilish tayinlash oynasida eski ro'yxatni
      // qoldirardi — jimgina.
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all })
      form.resetFields()
      onClose()
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={isEdit ? t('hr.jobTitle.editTitle') : t('hr.jobTitle.createTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={520}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Form.Item
          name="name"
          label={t('hr.jobTitle.name')}
          rules={[
            { required: true, message: t('hr.jobTitle.validation.nameRequired') },
            { max: NAME_MAX, message: t('hr.person.validation.nameLength') },
          ]}
        >
          <Input autoFocus />
        </Form.Item>

        <Form.Item
          name="code"
          label={t('hr.jobTitle.code')}
          extra={t('hr.jobTitle.codeHint')}
          rules={[
            { required: true, message: t('hr.jobTitle.validation.codeRequired') },
            // `HrTextRules.JOB_TITLE_CODE` ning nusxasi; saqlashda katta harfga o'tadi.
            {
              validator: (_rule, value: string | undefined) =>
                !value || JOB_TITLE_CODE_PATTERN.test(value.trim().toUpperCase())
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('hr.jobTitle.validation.codeFormat'))),
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="active"
          label={t('hr.jobTitle.active')}
          valuePropName="checked"
          extra={t('hr.jobTitle.activeHint')}
        >
          <Switch />
        </Form.Item>

        {isEdit && jobTitle && jobTitle.personCount > 0 ? (
          <Alert
            type="info"
            showIcon
            message={t('hr.jobTitle.inUseNote', { count: jobTitle.personCount })}
          />
        ) : null}
      </Form>
    </Modal>
  )
}
